import React, { useEffect, useState } from 'react';
import EnhancedTable from 'src/components/enhanced-table/EnhancedTable';
import {
  EnhancedTableColumn,
  EnhancedTableColumnType,
  ITableMenuAction
} from 'src/components/enhanced-table';
import { DataService } from '../../services/data-service';
import Loader from 'src/components/Loader';
import MainPageTitle from 'src/components/layout-components/main-layout/MainPageTitle';
import { toast } from 'react-toastify';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import StatWidget from 'src/components/widgets/common/StatWidget';
import { Delete, SpeakerGroupOutlined } from '@mui/icons-material';
import PermissionService, { ACTION, ROLE } from 'src/services/permission-service';
import { useAppSelector } from 'src/redux/hooks';
import { Client, ClientApiResponse, ClientsApiResponse } from 'src/types/client.types';

const ClientListing: React.FC = () => {
  const [Clients, setClients] = useState<Record<string, Client>>({});
  const [loading, setLoading] = useState(true);
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [updateData, setUpdateData] = useState<Client | undefined>(undefined);
  const userRole = useAppSelector(s => s.user.user.role) as ROLE;

  const widgetVisibility = {
    totalClients: false,
  };

  // Fetch Clients
  const initialize = async () => {
    setLoading(true);
    try {
      const res = await DataService.get('api/Client');
      const response: ClientsApiResponse = await res.json();
      const data: ClientApiResponse[] = response.data || [];
      const result: Record<string, Client> = {};
      data.forEach((w) => {
        result[w.id] = {
          Id: w.id,
          Name: w.name
        };
      });
      setClients(result);
    } catch (e) {
      toast.error('Failed to fetch Clients');
    }
    setLoading(false);
  };

  useEffect(() => {
    initialize();
  }, []);


  // Export handler
  const handleExport = async () => {
    try {
      const response = await DataService.get('api/Client/export', {});
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Clients_export.xlsx';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } else {
        toast.error('Export failed');
      }
    } catch {
      toast.error('Export failed');
    }
  };

  // Table headers
  const headers: EnhancedTableColumn[] = [
    {
      id: 'Name',
      label: 'Name',
      type: EnhancedTableColumnType.TEXT,
      editable: true,
      required: true,
    },
    {
      id: 'actions',
      label: 'Actions',
      type: EnhancedTableColumnType.ACTION,
      actions: [
        // No edit action here; only add other actions if needed (e.g., delete)
      ]
    }
  ];

  // Inline add handler
  const handleAddClient = async (row: { Name: string }) => {
    const req = await DataService.post('api/Client/create', { Name: row.Name });
    const resJson = await req.json();
    if (req.ok && resJson.success) {
      await initialize();
      return true;
    } else {
      throw new Error(resJson.message || 'An Error occurred!');
    }
  };

  // Inline edit handler
  const handleEditClient = async (row: { id: string, Name: string }) => {
    const req = await DataService.put(`api/Client/update/${row.id}`, { Name: row.Name });
    const resJson = await req.json();
    if (req.ok && resJson.success) {
      await initialize();
      return true;
    } else {
      throw new Error(resJson.message || 'An Error occurred!');
    }
  };

  // Table actions
  const actions: ITableMenuAction[] = [
    // The add button is now handled by EnhancedTable's inline add
    {
      key: 'export',
      title: '',
      icon: <DownloadIcon />, 
      disabled: (selected: string[]) => false,
      onClick: handleExport,
    }
  ];

  // Prepare data for EnhancedTable
  const dataObj: Record<string, any> = Object.fromEntries(
    Object.entries(Clients).map(([id, Client]) => [
      id,
      {
        ...Client,
      }
    ])
  );


  if (loading) {
    return <Loader/>;
  }

  return (
    <div >
      <title>Clients</title>
      {/* Widgets Row */}
      <div style={{ display: 'flex', gap: 24, marginBottom: 32, flexWrap: 'wrap' }}>
        {widgetVisibility.totalClients && (
          <StatWidget
            title="Total Clients"
            value={Object.keys(Clients).length}
            icon={<SpeakerGroupOutlined />}
            color="#1976d2"
            visible={widgetVisibility.totalClients}
          />
        )}
      </div>
      {/* End Widgets Row */}
      <EnhancedTable
        title="Clients"
        columns={headers}
        data={dataObj}
        actions={actions}
        onAddRow={handleAddClient}
        onEditRow={handleEditClient}
        showEditOnTable={true}
        collapsible={true}
        striped={true}
        boxedCells={true}
        wmsMode={true}
      />
    </div>
  );
};

export default ClientListing;
